// frontend/src/ts/protocols/persistence/browserWriteStore.ts
import {PendingSummary, PendingWrite, WriteType} from './types'
import {ClaimArgs, EnqueueArgs, GetPendingWritesArgs, WriteStore} from './writeStore'
import {deepMerge} from '@/utils/merging'

interface ProtocolEntry {
    writes: PendingWrite[]
    nextId: number
}

// Process-wide map of onWritePersisted subscribers, keyed by `${co}:${pr}`.
// Session-only — never persisted to localStorage (handlers are typically
// closures over React state and can't survive reload anyway).
const persistHandlers = new Map<string, Set<(rowId: number, coalesceKey: string | null, writeType: WriteType, response: unknown) => void>>()

function persistHandlerKey(companyId: string, protocolId: string): string {
    return `${companyId}:${protocolId}`
}

function emptyEntry(): ProtocolEntry {
    return {writes: [], nextId: 1}
}

function storageKey(companyId: string, protocolId: string): string {
    return `neurotune:write-store:${companyId}:${protocolId}`
}

function entryKey(companyId: string, protocolId: string): string {
    return `${companyId}:${protocolId}`
}

/**
 * Hybrid browser-mode WriteStore:
 * - localStorage is the durable backing; survives page refreshes.
 * - In-memory Map caches each ProtocolEntry for fast access; hydrated from
 *   localStorage on first access for a given (companyId, protocolId).
 * - If localStorage is unavailable (Safari private mode, quota exceeded,
 *   disabled in settings) the in-memory cache becomes authoritative and
 *   persistence is silently disabled for the rest of the session.
 *
 * Semantics mirror the Rust commands.rs operations exactly: enqueue
 * drops unclaimed pending/dead rows with the same coalesce_key + write_type
 * before inserting the new row (claimed pending rows stay, matching the
 * Rust `enqueue_write` SQL); claim picks the earliest pending row per
 * coalesce_key whose last_attempt_at is past the cutoff; markFailed
 * transitions to pending (retryable) or dead (terminal).
 *
 * The enqueue cascade filters on write_type: an event_delete does NOT
 * supersede a prior event_create, both reach the drainer, the server
 * creates then deletes (eventually consistent; avoids the in-flight
 * race where the create was already claimed and dispatched but the
 * supersede silently transitions the row from superseded → persisted
 * after the server already created the event).
 *
 * Storage lifecycle (browser mode only — TauriWriteStore retains history):
 * - Superseded rows are *removed* (not marked) by enqueue; the
 *   coalescing cascade immediately drops the prior pending/dead rows
 *   from both the in-memory entry and localStorage.
 * - markPersisted removes the row from the in-memory entry and
 *   localStorage. Once the entry is empty, the localStorage key is
 *   dropped entirely.
 * - On hydrate, persisted and superseded rows are swept from the loaded
 *   entry and the cleaned JSON is written back so users with pre-fix
 *   accumulated data see localStorage shrink on first page load.
 *
 * Net effect: storage usage is bounded by the number of in-flight
 * writes (pending + dead) at any moment, not by total writes ever
 * enqueued.
 */
export class BrowserWriteStore implements WriteStore {
    private readonly storageOk: boolean
    private persistenceDisabled = false
    private readonly entries = new Map<string, ProtocolEntry>()

    constructor() {
        this.storageOk = this.probeStorage()
    }

    private probeStorage(): boolean {
        try {
            const probe = '__neurotune_probe__'
            window.localStorage.setItem(probe, '1')
            window.localStorage.removeItem(probe)
            return true
        } catch {
            return false
        }
    }

    private sweepStaleRows(writes: PendingWrite[]): PendingWrite[] {
        return writes.filter((w) => w.state !== 'persisted' && w.state !== 'superseded')
    }

    /** Unclaimed pending, or dead — claimed pending (HTTP in flight) is not a cascade target. */
    private isCascadeTarget(w: PendingWrite): boolean {
        return w.state === 'dead' || (w.state === 'pending' && w.lastAttemptAt == null)
    }

    private rawWrite(companyId: string, protocolId: string, writes: PendingWrite[], nextId: number): void {
        if (!this.storageOk || this.persistenceDisabled) return
        try {
            window.localStorage.setItem(
                storageKey(companyId, protocolId),
                JSON.stringify({writes, nextId}),
            )
        } catch (e) {
            this.persistenceDisabled = true
            console.warn('[browserWriteStore] localStorage unavailable; queue is in-memory only for this session', e)
        }
    }

    private rawRemove(companyId: string, protocolId: string): void {
        if (!this.storageOk || this.persistenceDisabled) return
        try {
            window.localStorage.removeItem(storageKey(companyId, protocolId))
        } catch {
            // Best-effort; ignore — the entry is already empty in memory.
        }
    }

    private loadEntry(companyId: string, protocolId: string): ProtocolEntry | null {
        if (!this.storageOk) return null
        try {
            const raw = window.localStorage.getItem(storageKey(companyId, protocolId))
            if (!raw) return null
            const parsed = JSON.parse(raw) as { writes: PendingWrite[]; nextId: number }
            const originalWrites = parsed.writes ?? []
            const cleaned = this.sweepStaleRows(originalWrites)
            const nextId = parsed.nextId ?? 1
            // Persist the cleaned version back so localStorage stays in
            // sync with the in-memory entry (one-time cleanup of pre-fix
            // data). If nothing was swept, leave localStorage untouched.
            if (cleaned.length < originalWrites.length) {
                if (cleaned.length === 0) {
                    this.rawRemove(companyId, protocolId)
                } else {
                    this.rawWrite(companyId, protocolId, cleaned, nextId)
                }
            }
            return {writes: cleaned, nextId}
        } catch {
            return null
        }
    }

    private persistEntry(companyId: string, protocolId: string, entry: ProtocolEntry): void {
        // Empty entries don't deserve a localStorage key — drop it entirely
        // and forget the in-memory entry so the next access creates a fresh one.
        if (entry.writes.length === 0) {
            this.entries.delete(entryKey(companyId, protocolId))
            this.rawRemove(companyId, protocolId)
            return
        }
        this.rawWrite(companyId, protocolId, entry.writes, entry.nextId)
    }

    private getEntry(companyId: string, protocolId: string): ProtocolEntry {
        const k = entryKey(companyId, protocolId)
        let e = this.entries.get(k)
        if (!e) {
            e = this.loadEntry(companyId, protocolId) ?? emptyEntry()
            this.entries.set(k, e)
        }
        return e
    }

    async enqueue(args: EnqueueArgs): Promise<number> {
        const e = this.getEntry(args.companyId, args.protocolId)

        // Merge-on-enqueue: capture `changes` from rows the cascade will
        // drop (unclaimed pending + dead) BEFORE removing them. The new
        // row's `changes` becomes the cumulative burst history for the
        // (coalesceKey, writeType) bucket. The caller's fresh
        // `args.changes` always wins on key conflict (deep merge). This
        // mirrors the Rust `enqueue_write_sql` in ndc/src-tauri/...
        // /commands.rs so the two backends produce identical row shapes.
        //
        // Iteration order: insertion order (e.writes array order). The
        // cascade below uses the same iteration order to supersede, so
        // the merge picks up exactly the rows the cascade drops. We
        // iterate in array order rather than sorting by createdAt to
        // keep the JS test fixture's `e.writes` order authoritative.
        let priorChanges: Record<string, unknown> | null = null
        if (args.coalesceKey) {
            for (const w of e.writes) {
                if (
                    w.coalesceKey === args.coalesceKey &&
                    w.writeType === args.writeType &&
                    this.isCascadeTarget(w) &&
                    w.changes
                ) {
                    priorChanges = deepMerge(priorChanges ?? {}, w.changes)
                }
            }
        }

        if (args.coalesceKey) {
            // Coalesce cascade: drop unclaimed pending + dead rows for
            // the same coalesce_key + write_type. The new write replaces
            // them. Used by step_update bursts (only the latest edit
            // should reach the server).
            //
            // Claimed pending rows (lastAttemptAt set) stay. Claim does
            // not change state, so treating every pending row as
            // replaceable lets Skip All drop a click-through write whose
            // PUT is already in flight; a later drain then sends SKIPPED
            // concurrently and last HTTP wins on the server.
            //
            // It does NOT cascade across different write types — an
            // event_delete for a coalesce_key with a pending event_create
            // does NOT supersede the create, so the server gets both
            // writes (eventually consistent).
            e.writes = e.writes.filter(
                (w) => !(
                    w.coalesceKey === args.coalesceKey &&
                    w.writeType === args.writeType &&
                    this.isCascadeTarget(w)
                )
            )
        }

        // Final `changes`: the caller's fresh changes overlaid on the
        // prior burst history. The caller wins on key conflict
        // (deepMerge's overlay argument is the second one, so its
        // keys win on tie). When the caller passed no `changes`
        // (event_create / event_delete shape, or no caller intent
        // to track edits), the prior history alone is preserved if
        // non-null; otherwise undefined.
        let finalChanges: Record<string, unknown> | undefined
        if (args.changes) {
            finalChanges = deepMerge(priorChanges ?? {}, args.changes)
        } else if (priorChanges) {
            finalChanges = priorChanges
        }

        const id = e.nextId++
        const write: PendingWrite = {
            id,
            coalesceKey: args.coalesceKey,
            stepId: args.stepId,
            writeType: args.writeType,
            payload: args.payload,
            // For step/protocol updates: the user-edit delta. Undefined
            // for event_create / event_delete (the whole payload is
            // the change).
            changes: finalChanges,
            createdAt: Date.now(),
            lastAttemptAt: null,
            attemptCount: 0,
            lastError: null,
            state: 'pending',
        }
        e.writes.push(write)
        this.persistEntry(args.companyId, args.protocolId, e)
        return id
    }

    async claim(args: ClaimArgs): Promise<PendingWrite[]> {
        // Mirrors the Rust `claim_pending_writes` SQL contract:
        //
        //   WITH ranked AS (
        //     SELECT id, created_at, ROW_NUMBER() OVER (
        //       PARTITION BY COALESCE(coalesce_key, '')
        //       ORDER BY created_at ASC, id ASC
        //     ) AS rn
        //     FROM pending_writes
        //     WHERE state = 'pending' OR state = 'dead'
        //   )
        //   ... WHERE rn = 1 AND state = 'pending' AND <backoff filter>
        //
        // Two steps:
        //   1. For each coalesce_key, find the EARLIEST row across
        //      `pending` AND `dead` (the "rn=1" rank). A pending row's
        //      rn=1 status is conditional on no earlier dead row in the
        //      same bucket.
        //   2. Filter to state='pending' (the head's state) AND
        //      past-cutoff. If the head is dead, the entire bucket is
        //      blocked until recovery via retryWrite(id) or
        //      bulkRetryDead() — the "dead-bucket block". See
        //      commands.rs claim_pending_writes doc for the rationale.
        //
        // Notes:
        //   - Persisted rows are excluded from the ranking window so
        //     a [persisted, pending] bucket is claimable. The persisted
        //     row is terminal; the pending row gets rn=1 in its own
        //     bucket (the dead-bucket block targets `dead`, not all
        //     terminal states).
        //   - Superseded rows don't exist in this store (the cascade
        //     removes them, not marks them).
        //   - NULL coalesce_keys are bucketed together (treated as one
        //     shared key) via the `?? ''` fallback.
        const e = this.getEntry(args.companyId, args.protocolId)
        const cutoff = args.backoffCutoffMs
        const now = Date.now()
        // Step 1: head-of-key (pending or dead) by (created_at ASC, id ASC).
        const headByKey = new Map<string, PendingWrite>()
        for (const w of e.writes) {
            if (w.state !== 'pending' && w.state !== 'dead') continue
            const key = w.coalesceKey ?? ''
            const existing = headByKey.get(key)
            if (
                !existing ||
                w.createdAt < existing.createdAt ||
                (w.createdAt === existing.createdAt && w.id < existing.id)
            ) {
                headByKey.set(key, w)
            }
        }
        // Step 2: head must be pending AND past cutoff. A dead head
        // blocks the entire bucket (dead-bucket block).
        const candidates: PendingWrite[] = []
        for (const head of headByKey.values()) {
            if (head.state !== 'pending') continue
            if (head.lastAttemptAt !== null && !(head.lastAttemptAt < cutoff)) continue
            candidates.push(head)
        }
        // Sort globally by (created_at, id) for cross-key dispatch ordering.
        candidates.sort((a, b) => {
            if (a.createdAt !== b.createdAt) return a.createdAt - b.createdAt
            return a.id - b.id
        })
        const claimed = candidates.slice(0, args.limit)
        for (const c of claimed) c.lastAttemptAt = now
        this.persistEntry(args.companyId, args.protocolId, e)
        return claimed
    }

    async markPersisted(companyId: string, protocolId: string, id: number): Promise<void> {
        const e = this.getEntry(companyId, protocolId)
        const idx = e.writes.findIndex((w) => w.id === id)
        if (idx === -1) return
        e.writes.splice(idx, 1)
        this.persistEntry(companyId, protocolId, e)
    }

    async markFailed(companyId: string, protocolId: string, id: number, error: string, retryable: boolean): Promise<void> {
        const e = this.getEntry(companyId, protocolId)
        const w = e.writes.find((row) => row.id === id)
        if (!w) return
        w.state = retryable ? 'pending' : 'dead'
        w.attemptCount += 1
        w.lastError = error
        w.lastAttemptAt = Date.now()
        this.persistEntry(companyId, protocolId, e)
    }

    async retryWrite(companyId: string, protocolId: string, id: number): Promise<void> {
        const e = this.getEntry(companyId, protocolId)
        const w = e.writes.find((row) => row.id === id)
        if (!w || w.state !== 'dead') return
        w.state = 'pending'
        w.lastError = null
        this.persistEntry(companyId, protocolId, e)
    }

    async retryAllDead(companyId: string, protocolId: string): Promise<number[]> {
        const e = this.getEntry(companyId, protocolId)
        const retried: number[] = []
        for (const w of e.writes) {
            if (w.state === 'dead') {
                w.state = 'pending'
                w.lastError = null
                retried.push(w.id)
            }
        }
        if (retried.length > 0) this.persistEntry(companyId, protocolId, e)
        return retried
    }

    async summary(companyId: string, protocolId: string): Promise<PendingSummary> {
        const e = this.getEntry(companyId, protocolId)
        let pending = 0, dead = 0, superseded = 0, persisted = 0
        for (const w of e.writes) {
            switch (w.state) {
                case 'pending':
                    pending++;
                    break
                case 'dead':
                    dead++;
                    break
                case 'superseded':
                    superseded++;
                    break
                case 'persisted':
                    persisted++;
                    break
            }
        }
        return {pending, dead, superseded, persisted}
    }

    async getPendingWrites(args: GetPendingWritesArgs): Promise<PendingWrite[]> {
        const e = this.getEntry(args.companyId, args.protocolId)
        const stateFilters = args.stateFilter
        const matching = e.writes.filter((w) => stateFilters.includes(w.state))
        let result: PendingWrite[]
        if (args.coalesceView) {
            // Latest row per coalesce_key, ordered by createdAt ASC.
            const byKey = new Map<string, PendingWrite>()
            for (const w of matching) {
                const key = w.coalesceKey ?? ''
                const existing = byKey.get(key)
                if (!existing || w.createdAt > existing.createdAt) {
                    byKey.set(key, w)
                }
            }
            result = [...byKey.values()].sort((a, b) => a.createdAt - b.createdAt)
        } else {
            result = [...matching].sort((a, b) => a.createdAt - b.createdAt)
        }
        // Mirror Rust's `args.limit.unwrap_or(1000)` — same default for both
        // stores so callers don't see different sized responses per backend.
        const limit = args.limit ?? 1000
        return result.slice(0, limit)
    }

    onWritePersisted(
        companyId: string,
        protocolId: string,
        handler: (rowId: number, coalesceKey: string | null, writeType: WriteType, response: unknown) => void,
    ): () => void {
        const k = persistHandlerKey(companyId, protocolId)
        let set = persistHandlers.get(k)
        if (!set) {
            set = new Set()
            persistHandlers.set(k, set)
        }
        set.add(handler)
        return () => {
            const s = persistHandlers.get(k)
            if (!s) return
            s.delete(handler)
            if (s.size === 0) persistHandlers.delete(k)
        }
    }

    notifyWritePersisted(
        companyId: string,
        protocolId: string,
        rowId: number,
        coalesceKey: string | null,
        writeType: WriteType,
        response: unknown,
    ): Promise<void> {
        const set = persistHandlers.get(persistHandlerKey(companyId, protocolId))
        if (!set || set.size === 0) return Promise.resolve()
        const snapshot = [...set]
        return Promise.all(
            snapshot.map((h) => Promise.resolve(h(rowId, coalesceKey, writeType, response))),
        ).then(() => undefined)
    }
}
